/**
 * declarationVersionText — КАК се изписва версията, на езика на четеца.
 *
 * Отделен модул от `declarationVersion.ts` нарочно: онзи отговаря на въпроса
 * „КОЯ е версията" и работи с числа и милисекунди; този отговаря на „КАК се
 * ИЗПИСВА" и работи с низове, локали и преводи. Слети, вторият удвоява първия и
 * файлът става място, където всеки добавя каквото му трябва.
 *
 * ── Двата дефекта, заради които съществува ─────────────────────────────────
 *
 * 🔴 **Б1 — един низ за два езика.** Стойността се сглобяваше веднъж, на
 * български, и се подаваше и на немската секция на документа. Немски родител би
 * прочел „Ich habe die Erklärung (8 от 16.09.2026 г.) gelesen" — българска
 * стойност вътре в немско ИЗРЕЧЕНИЕ, което той приема. Това е дефектът на
 * задача 17 в нова форма: стойност, влязла в контекст, за който не е
 * предназначена, и изглеждаща авторитетно.
 *
 * 🔴 **Б4 — един низ, два смисъла.** Същата стойност отиваше и до етикета
 * „Версия на декларацията: ", и вътре в текста на декларацията. До етикета тя
 * трябва да е ГОЛА („8 от 16.09.2026 г."), защото думата вече е в етикета;
 * вътре в текста трябва да е САМОСТОЯТЕЛНА („версия 8 от 16.09.2026 г."),
 * защото стои сама в скоби. Голото число в правен текст се чете като номер на
 * алинея или препратка към бележка — намерено 16.09 върху ОТВОРЕН документ,
 * не от анализ.
 *
 * ── Защо префиксът е в СТОЙНОСТТА, а не в текста на декларацията ───────────
 *
 * Трите стойности са различни части на речта: две са обозначения („8 от
 * 16.09.2026 г.", „1"), третата е изречение („преди въвеждане на версии").
 * Написан в текста, префиксът дава „версия преди въвеждане на версии". Написан
 * в стойността, условно, работи и в трите случая.
 *
 * ── Един четец за двете страни ─────────────────────────────────────────────
 *
 * Документът и екранът за подписване викат ЕДНА функция. Дублирано
 * форматирането на клиента, екранът би казал „версия 8", а документът „версия 8
 * от 16.09.2026 г." — и разминаването се вижда чак когато родителят сравни
 * подписаното с полученото.
 *
 * ⚠️ Часовата зона е `Europe/Vienna` за ВСИЧКИ езици: тя е физическото място на
 * училището, не езикът на четеца. Заковано е и с гард.
 */

import type { Language } from '../types';

/** Часовата зона на училището. Не зависи от езика — виж горе. */
export const DOCUMENT_TIME_ZONE = 'Europe/Vienna';

/**
 * Низовете за версията, по език.
 *
 * 🔴 Преводите на `de` и `en` са ПРЕДЛОЖЕНИ, не одобрени (16.09.2026). Влизат в
 * правно обвързващ документ и се потвърждават от owner-а и от юрист. Стоят на
 * ЕДНО място точно за да може потвърждението да смени низ, а не конструкция.
 *
 * ⚠️ Българските са ЖИВИ — те са на production от задача 16 и не се променят
 * от тази задача.
 */
interface VersionStrings {
  /**
   * Пред номера, когато стойността стои САМОСТОЯТЕЛНО в текста на декларацията.
   * Празен низ значи „без префикс" — виж `beforeVersioning`.
   */
  readonly inlinePrefix: string;
  /** Свързващата дума пред датата на публикуване. */
  readonly datePreposition: string;
  /** Подпис отпреди въвеждането на версиите — цяло изречение, БЕЗ префикс. */
  readonly beforeVersioning: string;
  /** Текстът не е снимен при подписването (Р1 от задача 17). */
  readonly contentNotPreserved: string;
  /** Локалът за датата. Зоната е обща — виж `DOCUMENT_TIME_ZONE`. */
  readonly dateLocale: string;
}

const STRINGS: Record<Language, VersionStrings> = {
  bg: {
    inlinePrefix: 'версия ',
    datePreposition: ' от ',
    beforeVersioning: 'преди въвеждане на версии',
    contentNotPreserved: 'текстът не е съхранен при подписването',
    dateLocale: 'bg-BG',
  },
  de: {
    // Потвърдено от owner-а, 27.09 (`PDF-Rahmentexte — Deutsch.md`, № 23–26).
    inlinePrefix: 'Fassung ',
    datePreposition: ' vom ',
    beforeVersioning: 'vor Einführung der Fassungsnummerierung',
    contentNotPreserved: 'Text bei der Unterzeichnung nicht gespeichert',
    // `de-AT`, не `de-DE`: училището е в Австрия и родителите четат австрийски
    // документи. Форматът на датата съвпада, но локалът е по-честният.
    dateLocale: 'de-AT',
  },
  en: {
    // Потвърдено от owner-а, 27.09 (`PDF frame strings — English.md`, № 23–26):
    // „dated“ вместо „of“ — датата не се чете като втори номер на версия.
    inlinePrefix: 'version ',
    datePreposition: ' dated ',
    beforeVersioning: 'before versioning was introduced',
    contentNotPreserved: 'text not stored when signed',
    // `en-GB` дава 16/09/2026; `en-US` би дал 09/16/2026 — двусмислено за
    // европейски читател при ден ≤ 12.
    dateLocale: 'en-GB',
  },
};

/** Низовете за един език, с резерв български при непознат вход. */
function stringsFor(language: Language | undefined): VersionStrings {
  return STRINGS[language ?? 'bg'] ?? STRINGS.bg;
}

/**
 * Локалът за дати по език — ЕДИНСТВЕНАТА таблица (`STRINGS` горе).
 *
 * 🔴 Изнесено за задача 24: родителските екрани форматираха пет дати на закован
 * `bg-BG`, тоест немски родител четеше „16 септември 2026 г.". Без този износ
 * единственият им изход беше ПЕТО мапване език→локал в кода на екрана — точно
 * дефектът, който задача 26 съществува да събере на едно място. Задача 26 стъпва
 * на този износ, вместо да прави втори.
 *
 * ⚠️ Чисто добавъчно: нито един низ и нито един подпис по-горе не е пипан.
 */
export function dateLocaleFor(language: Language | undefined): string {
  return stringsFor(language).dateLocale;
}

/** Датата в документа — локалът по език, зоната е винаги на училището. */
function formatVersionDate(date: Date, language: Language): string {
  return date.toLocaleDateString(stringsFor(language).dateLocale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: DOCUMENT_TIME_ZONE,
  });
}

/**
 * Стойността за ЕТИКЕТА в блока с данни („Версия на декларацията: ___").
 *
 * ГОЛА — без префикс, защото думата вече стои в етикета. „Версия на
 * декларацията: версия 8" е излишно повторение.
 *
 * Три случая, всеки честен за това, което се знае:
 * ```
 *   версия + дата   →  „8 от 16.09.2026 г."
 *   версия без дата →  „8"                      заварена, никой не е публикувал
 *   без версия      →  „преди въвеждане на версии"
 * ```
 */
export function versionLabelValue(
  version: number | undefined,
  publishedAt: Date | undefined,
  language: Language = 'bg',
): string {
  const s = stringsFor(language);
  if (version === undefined) return s.beforeVersioning;
  if (publishedAt === undefined) return String(version);
  return `${version}${s.datePreposition}${formatVersionDate(publishedAt, language)}`;
}

/**
 * Стойността за ПЛЕЙСХОЛДЪРА `{{version}}` вътре в текста на декларацията.
 *
 * САМОСТОЯТЕЛНА — носи префикса, защото стои сама в скоби и нищо наоколо не
 * казва какво е:
 * ```
 *   версия + дата   →  „версия 8 от 16.09.2026 г."
 *   версия без дата →  „версия 1"
 *   без версия      →  „преди въвеждане на версии"   ← БЕЗ префикс: изречение е
 * ```
 *
 * 🔴 Третият случай нарочно няма префикс. „версия преди въвеждане на версии" е
 * счупено, а точно той е случаят, който заварените подписи биха дали.
 */
export function versionInlineValue(
  version: number | undefined,
  publishedAt: Date | undefined,
  language: Language = 'bg',
): string {
  const s = stringsFor(language);
  if (version === undefined) return s.beforeVersioning;
  return `${s.inlinePrefix}${versionLabelValue(version, publishedAt, language)}`;
}

/** Признанието, че текстът не е снимен при подписването — на езика на четеца. */
export function contentNotPreservedValue(language: Language = 'bg'): string {
  return stringsFor(language).contentNotPreserved;
}

/**
 * Плейсхолдърът за версия в текста на декларацията.
 *
 * 🔴 Двойни скоби, макар останалите системни плейсхолдъри да са с единични.
 * Единичната скоба се среща в обикновен текст (списъци, бележки) и не може да
 * се чисти безопасно; `{{…}}` е семейството, което този проект вече пази.
 *
 * ⚠️ Отделен е от `{{field:…}}` (`frontend/src/utils/placeholders.ts`) — онзи
 * хваща интерактивни полета и НЕ познава този. Затова текст, който съдържа само
 * `{{version}}`, минава по обикновения път на рендера.
 */
export const VERSION_PLACEHOLDER = /\{\{version\}\}/g;

/**
 * Заменя `{{version}}` в текст — единствената функция, която двете страни викат.
 *
 * 🔴 За ЕКРАНА се вика при РЕНДЕР, не при зареждане: снимката на текста, която
 * подписът пази, трябва да остане СУРОВА. Заместена преди снимката, тя става
 * препис вместо доказателство — документът вече не може да бъде възстановен от
 * нея, а само преписан, при това с форматирането на браузъра, чиято версия е
 * неизвестна (service worker сервира стар код с дни).
 *
 * Текст без плейсхолдър се връща непроменен — шестте живи декларации не го
 * носят и не бива да се променят от тази задача.
 */
export function renderVersionPlaceholder(
  text: string,
  version: number | undefined,
  publishedAt: Date | undefined,
  language: Language = 'bg',
): string {
  return text.replace(VERSION_PLACEHOLDER, versionInlineValue(version, publishedAt, language));
}
